import { createPortal } from "react-dom";
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useLabText, labField } from "../../lib/labText";
import { QuestionField } from "./QuestionField";

const toForm = (saved) => Object.fromEntries(Object.entries(saved || {}).map(([k, a]) => [k, {
  value: a.text ?? (a.type === "multi" ? (a.selected || []).map((s) => s.id) : a.selected?.[0]?.id), comment: a.comment || "" }]));

export const EvaluationDialog = ({ exp, questions, settings, state, onClose, onDone }) => {
  const { t } = useLabText();
  const s1 = settings.stage1_enabled, s2 = settings.stage2_enabled;
  const stage = s1 && state?.stage1 !== "submitted" ? 1 : 2;
  const [answers, setAnswers] = useState({});
  const [allow, setAllow] = useState(!!state?.allow_publish);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get(`/lab/evaluations/${exp.id}`).then((r) => {
      setAllow(!!r.data.allow_publish); setName(r.data.display_name || "");
      setAnswers((cur) => ({ ...toForm(r.data[`stage${stage}`]?.answers), ...cur }));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, [exp.id, stage]);
  const qs = questions.filter((q) => q.stage === stage && (exp.eval_category === "skin" || !q.skin_only));
  const send = async (draft) => {
    setBusy(true);
    try {
      await api.post("/lab/evaluations", { experiment_id: exp.id, stage, answers, draft, allow_publish: allow, display_name: name });
      toast.success(draft ? t("draft_saved") : t("submitted"));
      onDone();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Error");
    } finally {
      setBusy(false);
    }
  };
  if ((stage === 2 && !s2) || state?.[`stage${stage}`] === "submitted") return null;
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center md:p-6" onClick={onClose}>
      <div data-testid="lab-eval-dialog" onClick={(e) => e.stopPropagation()} className="bg-[#0B0A08] border border-[#262421] w-full md:max-w-lg max-h-[94vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div><p className="text-[10px] uppercase tracking-[0.3em] text-[#C49A6C]" dir="ltr">{exp.code}</p><h3 className="serif text-2xl mt-1">{stage === 1 ? t("stage1") : t("stage2")}</h3></div>
          <button onClick={onClose} data-testid="lab-eval-close" className="text-[#8A857D] hover:text-[#F2EBE1]"><X size={22} /></button>
        </div>
        <p className="text-xs text-[#C49A6C] mt-3 italic">{t("honest")}</p>
        <div className="space-y-8 mt-7">
          {loaded && qs.map((q, i) => <QuestionField key={q.id} q={q} n={i + 1} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />)}
        </div>
        <label className="flex items-start gap-3 mt-8 text-xs text-[#B8B0A4] cursor-pointer">
          <input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} data-testid="lab-eval-allow" className="mt-0.5 accent-[#C49A6C]" />{t("allow_publish")}
        </label>
        {allow && <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("display_name")} data-testid="lab-eval-name" className={`${labField} mt-3`} />}
        <div className="grid grid-cols-[auto_1fr] gap-3 mt-7">
          <button onClick={() => send(true)} disabled={busy} data-testid="lab-eval-draft" className="border border-[#4A4A46] text-[#B8B0A4] text-[11px] uppercase tracking-[0.2em] px-5 py-4 hover:border-[#C49A6C] transition-colors disabled:opacity-40">{t("save_draft")}</button>
          <button onClick={() => send(false)} disabled={busy} data-testid="lab-eval-submit" className="bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors disabled:opacity-40">{t("submit")}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
